import React, { useEffect, useState } from "react";

import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import { Attribute, ChildrenConfiguration, ObjectType } from "gql/generated/types";
import { InputText } from "primereact/inputtext";
import { TabPanel, TabView } from "primereact/tabview";
import { T } from "services/lang/LanguageService";
import { InputSwitch } from "primereact/inputswitch";
import AdditionalProjectSettings from "./AdditionalProjectSettings";

type NodesAttributesConfigurationProps = {
  childrenAttributes: ChildrenConfiguration[];
  setChildrenAttributes: React.Dispatch<React.SetStateAction<ChildrenConfiguration[]>>;
};

const NodesAttributesConfiguration: React.FC<NodesAttributesConfigurationProps> = ({
  childrenAttributes,
  setChildrenAttributes,
}) => {
  const nodes = Object.values(ObjectType).filter(nodeType => nodeType !== ObjectType.Object);

  return (
    <TabView className="col-12">
      {nodes.map(node => (
        <TabPanel header={node} key={node}>
          <Node
            type={node}
            childrenAttributes={childrenAttributes}
            setChildrenAttributes={setChildrenAttributes}
          />
        </TabPanel>
      ))}
    </TabView>
  );
};

export default NodesAttributesConfiguration;

const Node: React.FC<{ type: ObjectType } & NodesAttributesConfigurationProps> = ({
  type,
  childrenAttributes,
  setChildrenAttributes,
}) => {
  const node = childrenAttributes.find(a => a.childType === type);
  const [attributes, setAttributes] = useState<Attribute[]>(() => (node?.customFields as Attribute[]) ?? []);

  useEffect(() => {
    if (!node) {
      setChildrenAttributes(prev => {
        if (prev.find(a => a.childType === type)) {
          return prev;
        }
        return [...prev, { childType: type, customFields: [] }];
      });
    }
  }, [node, setChildrenAttributes, type]);

  useEffect(() => {
    setChildrenAttributes(prev =>
      prev.map(a => (a.childType === type ? { ...a, customFields: attributes } : a))
    );
  }, [attributes, setChildrenAttributes, type]);

  const handleChange = (key: keyof ChildrenConfiguration, value: string | boolean) => {
    setChildrenAttributes(prev => prev.map(a => (a.childType === type ? { ...a, [key]: value } : a)));
  };
  return (
    <>
      <div className="p-fluid grid mb-4">
        <InputLabelWrapper labelName={T("word.visible")} col={2} flexDirection="column" className="gap-2">
          <InputSwitch
            checked={Boolean(node?.visible)}
            onChange={e => handleChange("visible", e.value)}
            className="p-inputtext-sm"
          />
        </InputLabelWrapper>
        <InputLabelWrapper labelName={T("mdm.plural-node-name")} col={5}>
          <InputText
            value={node?.pluralName ?? ""}
            onChange={e => handleChange("pluralName", e.target.value)}
            className="p-inputtext-sm"
            name="pluralName"
            placeholder={T("mdm.plural-node-name")}
          />
        </InputLabelWrapper>
        <InputLabelWrapper labelName={T("mdm.singular-node-name")} col={5}>
          <InputText
            value={node?.singularName ?? ""}
            onChange={e => handleChange("singularName", e.target.value)}
            className="p-inputtext-sm"
            name="singularName"
            placeholder={T("mdm.singular-node-name")}
          />
        </InputLabelWrapper>
      </div>
      <AdditionalProjectSettings attributes={attributes} setAttributes={setAttributes} />
    </>
  );
};
